import { NewsMediaOrganization, WebSite, WithContext } from "schema-dts";

import { SeoConfig } from "@voxmedia/duet-context/context/config-context";
import { DefaultSEOSchema } from "@voxmedia/duet-framework/config/seo.config";
import { SEOSchemaConfigType } from "@voxmedia/duet-framework/seo/components/ConfiguredSEOSchemas";

export const DEFAULT_SEO: DefaultSEOSchema = {
  title: "The Verge",
  description:
    "The Verge is about technology and how it makes us feel. Founded in 2011, we offer our audience everything from breaking news to reviews to award-winning features and investigations, on our site, in video, and in podcasts.",
  additionalMetaTags: [
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1, shrink-to-fit=no",
    },
    {
      name: "apple-mobile-web-app-title",
      content: "Verge",
    },
    {
      name: "google-site-verification",
      content: "IucFf_TKtbFFH8_YeFyEteQIwYPdANM1R46_U9DpAr4",
    },
  ],
  additionalLinkTags: [
    {
      rel: "alternate",
      type: "application/rss+xml",
      title: "The Verge",
      href: "/rss/index.xml",
    },
  ],
  facebook: {
    appId: "549923288395304",
  },
  openGraph: {
    images: [
      {
        url: "https://platform.theverge.com/wp-content/uploads/sites/2/2024/10/the_verge_social_share.png",
      },
    ],
    site_name: "The Verge",
    type: "website",
    url: "https://www.theverge.com/",
  },
  twitter: {
    site: "@verge",
    cardType: "summary_large_image",
  },
};

const NewsMediaOrganizationSchema: WithContext<NewsMediaOrganization> = {
  "@context": "https://schema.org",
  "@type": "NewsMediaOrganization",
  "@id": "https://www.theverge.com/#publisher",
  name: "The Verge",
  url: "https://www.theverge.com",
  description:
    "The Verge is a technology and science news website founded in 2011. It offers its readers breaking tech news, tech reviews, award-winning features and investigations through articles, videos, podcasts, and newsletters.",
  alternateName: ["Verge", "theverge"],
  logo: {
    "@type": "ImageObject",
    url: "https://cdn.vox-cdn.com/uploads/chorus_asset/file/24018771/verge_duet_500.png",
    height: "120",
    width: "500",
  },
  ethicsPolicy: "https://www.theverge.com/ethics-statement",
  masthead: "https://www.theverge.com/about-the-verge",
  foundingDate: "2011-11-01",
  founder: [
    { "@type": "Person", name: "Jim Bankoff" },
    { "@type": "Person", name: "Joshua Topolsky" },
    { "@type": "Person", name: "Marty Moe" },
    { "@type": "Person", name: "Nilay Patel" },
    { "@type": "Person", name: "Joanna Stern" },
    { "@type": "Person", name: "Dieter Bohn" },
    { "@type": "Person", name: "Joseph L. Flatley" },
    { "@type": "Person", name: "Sean Hollister" },
    { "@type": "Person", name: "Thomas Houston" },
    { "@type": "Person", name: "Laura June" },
    { "@type": "Person", name: "Paul Miller" },
    { "@type": "Person", name: "Ross Miller" },
    { "@type": "Person", name: "Thomas Ricker" },
    { "@type": "Person", name: "Vald Savov" },
    { "@type": "Person", name: "Chris Ziegler" },
  ],
  ownershipFundingInfo:
    "The Verge is owned by Vox Media, the leading modern media company dedicated to high-quality journalism.",
  sameAs: [
    "https://www.facebook.com/verge/",
    "https://twitter.com/verge",
    "https://www.instagram.com/verge",
    "https://www.youtube.com/user/theverge",
    "https://www.linkedin.com/company/the-verge",
    "https://www.wikidata.org/wiki/Q2155442",
    "https://en.wikipedia.org/wiki/The_Verge",
  ],
  knowsAbout: [
    { "@type": "Thing", name: "technology" },
    { "@type": "Thing", name: "tech news" },
    { "@type": "Thing", name: "tech products" },
    { "@type": "Thing", name: "tech events" },
    { "@type": "Thing", name: "consumer electronics" },
    { "@type": "Thing", name: "gadgets" },
    { "@type": "Thing", name: "software news" },
    { "@type": "Thing", name: "tech reviews" },
    { "@type": "Thing", name: "the verge reviews" },
    { "@type": "Thing", name: "phone reviews" },
    { "@type": "Thing", name: "laptop reviews" },
    { "@type": "Thing", name: "buying guides" },
    { "@type": "Thing", name: "science news" },
    { "@type": "Thing", name: "cybersecurity news" },
    { "@type": "Thing", name: "gaming news" },
    { "@type": "Thing", name: "digital creator news" },
    { "@type": "Thing", name: "social media platforms" },
    { "@type": "Thing", name: "streaming platforms" },
    { "@type": "Thing", name: "section 230" },
  ],
  award: [
    "The National Magazine Awards 2022 Winner in Best Digital Illustration",
    "The Pulitzer Prize 2020 Finalist in Feature Writing",
    "Gerald Loeb 2022 Award Winner in Feature",
    "Deadline Club Award 2021 Winner in Reporting by Independent Digital Media",
  ],
};

const WebsiteSchema: WithContext<WebSite> = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: "https://www.theverge.com/",
  name: "The Verge",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://www.theverge.com/search?q={search_term_string}",
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    "query-input": "required name=search_term_string",
  },
};

export const SEO_SCHEMA_CONFIG_PATH_MAP: SEOSchemaConfigType = {
  "/": [NewsMediaOrganizationSchema, WebsiteSchema],
  "/redesign-woo": [NewsMediaOrganizationSchema, WebsiteSchema],
  "/about-the-verge": [NewsMediaOrganizationSchema],
};

// TODO (dcorrigan): doublecheck this again before Verge launch
export const seoConfig: SeoConfig = {
  pagesCanonicalMap: {
    "/pages/community-guidelines": "/community-guidelines",
    "/pages/about-the-verge": "/about-the-verge",
    "/pages/ethics-statement": "/ethics-statement",
    "/pages/podcasts": "/podcasts",
  },
  defaultOgImage: "",
};
